import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, User, Camera, MapPin, Info, Youtube, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { toTitleCase } from '../utils/format';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// TikTok isn't in lucide-react — small inline SVG keeps us off any icon CDN.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M16.5 3c.32 2.31 1.86 4.03 4.09 4.2v2.72c-1.3.08-2.6-.24-3.8-.9v6.36c0 3.3-2.5 5.92-5.72 5.92S5.35 18.68 5.35 15.38c0-3.12 2.36-5.63 5.47-5.72.3 0 .6.02.9.07v2.83a2.94 2.94 0 0 0-.9-.15c-1.6 0-2.85 1.28-2.85 2.94 0 1.62 1.25 2.94 2.85 2.94 1.63 0 2.94-1.3 2.94-2.99V3h2.74z" />
  </svg>
);

// Static megamenu column meta (header + icon component). Items come from /api/menu-items.
const MENU_COLUMNS = [
  { group: 1, title: 'Discover Photos',   Icon: Camera },
  { group: 2, title: 'Explore Mauritius', Icon: MapPin },
  { group: 3, title: 'The Project',       Icon: Info },
];

const SOCIAL_LINKS = [
  { href: 'https://www.youtube.com/@explora-mauritius',          Icon: Youtube,    label: 'YouTube' },
  { href: 'https://www.facebook.com/groups/mauritiusphotos',     Icon: Facebook,   label: 'Facebook' },
  { href: 'https://www.tiktok.com/@explora_mauritius',           Icon: TikTokIcon, label: 'TikTok' },
];

const LEGAL_LINKS = [
  { to: '/terms',   label: 'Terms of Service' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/cookies', label: 'Cookie Policy' },
  { to: '/photo-rights', label: 'Photo Rights' },
];

const Navbar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTransparent, setIsTransparent] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [menuGroups, setMenuGroups] = useState({ 1: [], 2: [], 3: [] });
  const megaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API_BASE}/menu-items`);
        setMenuGroups(response.data.groups || { 1: [], 2: [], 3: [] });
      } catch (err) {
        console.log('Could not load menu items');
      }
    };
    fetchMenu();
  }, []);

  useEffect(() => {
    const isHome = location.pathname === '/';
    setIsTransparent(isHome);
    
    if (isHome) {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 100);
      };
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setIsScrolled(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) {
        setMegaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setMegaOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  // When the megamenu is open we always show the solid white bar (even at the
  // top of the home page) so the top strip matches the white megamenu panel.
  const showTransparent = isTransparent && !isScrolled && !megaOpen;
  
  const navClasses = showTransparent
    ? 'fixed top-0 left-0 right-0 z-50 bg-transparent'
    : 'sticky top-0 z-50 bg-white border-b border-gray-200';
  
  const textClasses = showTransparent ? 'text-white' : 'text-gray-700';
  const buttonHoverClasses = showTransparent ? 'hover:bg-white/20' : 'hover:bg-gray-100';
  // Left CTA: outlined button (border, no fill). Right CTA: dark filled button.
  const outlineButtonClasses = showTransparent
    ? 'border border-white/70 text-white bg-transparent hover:bg-white/15'
    : 'border border-gray-300 text-gray-700 bg-transparent hover:bg-gray-50';
  const darkButtonClasses = showTransparent
    ? 'bg-white text-black hover:bg-gray-100'
    : 'bg-gray-900 text-white hover:bg-gray-800';

  return (
    <nav className={navClasses}>
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <picture>
              <source srcSet="/static/img/explora-logo-v1.webp" type="image/webp" />
              <img 
                src="/explora-logo.png" 
                alt="explora logo" 
                className="h-[2.5rem] w-auto"
                width={123}
                height={40}
              />
            </picture>
          </Link>

          {/* Search Bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search photos, photographers, tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full bg-gray-100 border-none focus:bg-white focus:ring-2 focus:ring-gray-300"
              />
            </div>
          </form>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <Link to="/account">
                  <Button variant="ghost" className={`flex items-center space-x-2 ${outlineButtonClasses}`}>
                    {user.user_image_url ? (
                      <img 
                        src={user.user_image_url} 
                        alt={`${toTitleCase(user.user_display_name)} Photographer on Explora Mauritius`}
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span>{toTitleCase(user.user_display_name)}</span>
                  </Button>
                </Link>

                <Link to="/upload">
                  <Button variant="ghost" className={darkButtonClasses}>
                    Submit a photo
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login?next=/upload">
                  <Button variant="ghost" className={outlineButtonClasses}>
                    Submit a photo
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className={darkButtonClasses}>
                    Log in
                  </Button>
                </Link>
              </>
            )}

            {/* Hamburger megamenu trigger (replaces "The Project") */}
            <div className="relative" ref={megaRef}>
              <button
                type="button"
                onClick={() => setMegaOpen(!megaOpen)}
                aria-label="Open menu"
                data-testid="megamenu-trigger"
                className={`p-2 rounded-md transition-colors ${textClasses} ${buttonHoverClasses}`}
              >
                {megaOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              {megaOpen && (
                <div
                  className="fixed left-0 right-0 top-16 z-50 bg-white border-t border-b border-gray-200 shadow-lg"
                  data-testid="megamenu-panel"
                >
                  <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                      {MENU_COLUMNS.map((col) => {
                        const isWide = col.group === 3; // "The Project" → 2 sub-columns
                        return (
                        <div
                          key={col.group}
                          className={isWide ? 'md:col-span-2' : ''}
                          data-testid={`megamenu-col-${col.group}`}
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <col.Icon className="w-4 h-4 text-gray-900" aria-hidden="true" />
                            <strong className="text-sm text-gray-900">{col.title}</strong>
                          </div>
                          <ul className={isWide ? 'columns-2 gap-x-10' : 'space-y-1'}>
                            {(menuGroups[col.group] || []).map((item) => (
                              <li key={item.item_id} className={isWide ? 'mb-1 break-inside-avoid' : ''}>
                                <Link
                                  to={item.url}
                                  className="text-sm text-[#777] hover:text-gray-900 transition-colors"
                                  data-testid={`megamenu-link-${item.item_id}`}
                                >
                                  {item.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                        );
                      })}
                    </div>

                    {/* Bottom row: legal links + social icons */}
                    <div className="mt-10 pt-6 border-t border-gray-200 flex flex-col md:flex-row items-center md:justify-between space-y-4 md:space-y-0">
                      <div className="flex items-center space-x-3 text-[13px] text-[#777]" data-testid="megamenu-legal">
                        {LEGAL_LINKS.map((l, i) => (
                          <React.Fragment key={l.to}>
                            {i > 0 && <span aria-hidden="true">·</span>}
                            <Link to={l.to} className="hover:text-gray-900 transition-colors">
                              {l.label}
                            </Link>
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="flex items-center space-x-4" data-testid="megamenu-social">
                        {SOCIAL_LINKS.map((s) => (
                          <a
                            key={s.href}
                            href={s.href}
                            rel="me noopener"
                            target="_blank"
                            aria-label={s.label}
                            className="text-[#777] hover:text-gray-900 text-base transition-colors"
                          >
                            <s.Icon className="w-[18px] h-[18px]" aria-hidden="true" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 rounded-md ${buttonHoverClasses}`}
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${textClasses}`} />
            ) : (
              <Menu className={`w-6 h-6 ${textClasses}`} />
            )}
          </button>
        </div>

        {/* Mobile Search Bar */}
        <form onSubmit={handleSearch} className="md:hidden pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-full bg-gray-100 border-none"
            />
          </div>
        </form>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-2">
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button variant="outline" className="w-full justify-center" data-testid="mobile-account-btn">
                    <User className="w-4 h-4 mr-2" />
                    My Account
                  </Button>
                </Link>
                <Link to="/upload" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button variant="default" className="w-full justify-center" data-testid="mobile-submit-btn">Submit a photo</Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login?next=/upload" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button variant="outline" className="w-full justify-center" data-testid="mobile-submit-btn">Submit a photo</Button>
                </Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button variant="default" className="w-full justify-center" data-testid="mobile-login-btn">Log in</Button>
                </Link>
              </>
            )}
          </div>
            
            {/* Megamenu columns - Mobile */}
            <div className="pt-2 space-y-4">
              {MENU_COLUMNS.map((col) => {
                const isWide = col.group === 3; // "The Project" → 2 columns
                return (
                <div key={col.group} className="border-t border-gray-100 pt-3">
                  <div className="flex items-center space-x-2 px-4 py-1 mb-1">
                    <col.Icon className="w-4 h-4 text-gray-900" aria-hidden="true" />
                    <strong className="text-sm text-gray-900">{col.title}</strong>
                  </div>
                  <ul className={isWide ? 'grid grid-cols-2' : ''}>
                    {(menuGroups[col.group] || []).map((item) => (
                      <li key={item.item_id}>
                        <Link
                          to={item.url}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-1.5 text-sm text-[#777] hover:bg-gray-50"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                );
              })}

              {/* Mobile legal + social */}
              <div className="border-t border-gray-100 pt-4 px-4 space-y-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-[#777]">
                  {LEGAL_LINKS.map((l, i) => (
                    <React.Fragment key={l.to}>
                      {i > 0 && <span aria-hidden="true">·</span>}
                      <Link to={l.to} onClick={() => setMobileMenuOpen(false)} className="hover:text-gray-900">
                        {l.label}
                      </Link>
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex items-center space-x-4">
                  {SOCIAL_LINKS.map((s) => (
                    <a
                      key={s.href}
                      href={s.href}
                      rel="me noopener"
                      target="_blank"
                      aria-label={s.label}
                      className="text-[#777] hover:text-gray-900 text-base"
                    >
                      <s.Icon className="w-[18px] h-[18px]" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
